import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { X, Pizza, Loader2, Check } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { MenuItem, MenuItemOption } from '../../lib/types';
import { supabase } from '../../lib/supabase';
import { formatCurrency, cn } from '../../lib/utils';
/**
 * EmiliusFiveStationsModal
 * ───────────────────────────────────────────────────────────────────
 * Flujo dedicado para la pizza "5 Estaciones" de Emilius (rest. 27).
 *
 * Comportamiento:
 *   1. El cliente elige el TAMAÑO de la 5 Estaciones (usa las propias
 *      opciones de tamaño del item, igual que cualquier pizza normal).
 *   2. Selecciona EXACTAMENTE 5 sabores diferentes de la lista de
 *      especialidades. Los sabores son sólo decoración para el ticket
 *      de cocina — no alteran el precio, que lo determina el tamaño.
 *
 * Reglas defensivas:
 *   • Excluye "Extra queso", "Borde relleno" y la propia "5 Estaciones"
 *     de la lista de sabores elegibles.
 *   • Excluye "Chiniadita" para que no se mezcle (regla de negocio:
 *     Chiniadita sólo funciona como mitad-y-mitad, no como 1/5).
 *   • Manda la línea al cart como el menu_item real (la 5 Estaciones),
 *     así KDS, impresoras y caja la ven como un producto del menú con
 *     todos sus FKs intactos.
 */
const PIZZA_ADDON_NAMES = ['extra queso', 'borde relleno'];
function isPizzaAddon(name: string): boolean {
  const n = (name || '').toLowerCase().trim();
  return PIZZA_ADDON_NAMES.some(addon => n.includes(addon));
}
function isChiniadita(name: string): boolean {
  return (name || '').toLowerCase().includes('chiniadita');
}
function is5Estaciones(name: string): boolean {
  const n = (name || '').toLowerCase();
  return n.includes('5 estaciones') || n.includes('cinco estaciones');
}
const REQUIRED_FLAVORS = 5;
interface EmiliusFiveStationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Misma firma que handleAddToCart en los hosts:
  //   (item, quantity, options, notes)
  onAdd: (item: MenuItem, quantity: number, selectedOptions: any[], notes: string) => void;
  // El propio menu_item "5 Estaciones"
  fiveStationsItem: MenuItem;
  // Todas las pizzas disponibles para elegir como sabor
  pizzas: MenuItem[];
}
export function EmiliusFiveStationsModal({
  isOpen,
  onClose,
  onAdd,
  fiveStationsItem,
  pizzas
}: EmiliusFiveStationsModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedFlavors, setSelectedFlavors] = useState<MenuItem[]>([]);
  const [sizeOptions, setSizeOptions] = useState<MenuItemOption[]>([]);
  const [loading, setLoading] = useState(false);
  // Sabores elegibles — todas las pizzas menos addons, chiniadita y la
  // propia 5 Estaciones.
  const flavorPizzas = useMemo(() => pizzas.filter(p => !isPizzaAddon(p.name) && !isChiniadita(p.name) && !is5Estaciones(p.name)), [pizzas]);
  // Reset al abrir
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedSize('');
      setSelectedFlavors([]);
    }
  }, [isOpen]);
  // Cargar opciones de tamaño DE LA 5 ESTACIONES
  useEffect(() => {
    if (!isOpen || !fiveStationsItem?.id) return;
    let cancelled = false;
    const fetchSizes = async () => {
      setLoading(true);
      try {
        const {
          data,
          error
        } = await supabase.from('menu_item_options').select('*').eq('menu_item_id', fiveStationsItem.id).ilike('option_group', 'tamaño').eq('is_active', true).order('sort_order', {
          ascending: true
        });
        if (error) throw error;
        if (!cancelled) setSizeOptions(data || []);
      } catch (err) {
        console.error('[5Estaciones] fetch sizes error:', err);
        if (!cancelled) setSizeOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSizes();
    return () => {
      cancelled = true;
    };
  }, [isOpen, fiveStationsItem?.id]);
  // Precio final = precio base + delta del tamaño
  const finalPrice = useMemo(() => {
    const base = Number(fiveStationsItem?.price) || 0;
    if (!selectedSize) return base;
    const opt = sizeOptions.find(o => (o.name || '').toLowerCase().trim() === selectedSize.toLowerCase());
    const delta = opt ? Number(opt.price_delta) || 0 : 0;
    return base + delta;
  }, [fiveStationsItem?.price, selectedSize, sizeOptions]);
  const toggleFlavor = (pizza: MenuItem) => {
    setSelectedFlavors(prev => {
      const exists = prev.find(p => p.id === pizza.id);
      if (exists) return prev.filter(p => p.id !== pizza.id);
      if (prev.length >= REQUIRED_FLAVORS) return prev; // tope alcanzado
      return [...prev, pizza];
    });
  };
  const handleNext = () => {
    if (step === 1 && selectedSize) setStep(2);
  };
  const handleBack = () => {
    if (step === 2) setStep(1);
  };
  const handleConfirm = () => {
    try {
      if (!selectedSize || selectedFlavors.length !== REQUIRED_FLAVORS) return;
      const sizeLabel = selectedSize.charAt(0).toUpperCase() + selectedSize.slice(1);
      // Opciones que se envían al cart / KDS / ticket:
      //   1. El tamaño
      //   2. Cada uno de los 5 sabores como su propia "option" para
      //      que la cocina vea la lista clara y separada.
      const cartOptions: any[] = [{
        option_name: sizeLabel,
        option_group: 'Tamaño',
        price_delta: 0 // ya está dentro del precio base
      }, ...selectedFlavors.map((flavor, i) => ({
        option_name: flavor.name,
        option_group: `Sabor ${i + 1}/5`,
        price_delta: 0
      }))];
      // Las notas también listan los 5 sabores — defensa por si algún
      // sistema downstream sólo lee notes.
      const notes = selectedFlavors.map((f, i) => `Sabor ${i + 1}/5: ${f.name}`).join('\n');
      // Construimos un item efectivo: tomamos el menu_item real para
      // que el FK y el station_code lleguen bien, pero le pisamos el
      // precio con el del tamaño elegido.
      const effectiveItem: MenuItem = {
        ...fiveStationsItem,
        price: finalPrice
      };
      onAdd(effectiveItem, 1, cartOptions, notes);
      onClose();
    } catch (err) {
      console.error('[5Estaciones] handleConfirm failed:', err);
      // eslint-disable-next-line no-alert
      alert('No se pudo agregar la 5 Estaciones al pedido. Volvé a intentar.');
    }
  };
  const canConfirm = selectedFlavors.length === REQUIRED_FLAVORS;
  return <Modal isOpen={isOpen} onClose={onClose} maxWidth="md" data-id="element-2459">
      <div className="bg-[#0B0B0B] text-white overflow-hidden flex flex-col max-h-[85vh]" data-id="element-2460">
        {/* Header */}
        <div className="shrink-0 px-4 py-4 border-b border-white/10 flex items-center justify-between bg-[#111]" data-id="element-2461">
          <div className="min-w-0 pr-2" data-id="element-2462">
            <h2 className="text-lg font-black text-[#6FA248] uppercase tracking-wider truncate" data-id="element-2463">
              5 Estaciones
            </h2>
            <p className="text-xs text-white/50 mt-0.5" data-id="element-2464">
              Elegí 5 sabores diferentes — centro de salsa blanca y marinara de
              la casa
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors" data-id="element-2465">
            <X className="w-5 h-5" data-id="element-2466" />
          </button>
        </div>
        {/* Progress */}
        <div className="shrink-0 flex items-center px-4 py-3 bg-[#161616] border-b border-white/5" data-id="element-2467">
          {[1, 2].map(s => <Fragment key={s} data-id="element-2468">
              <div className={cn('w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors', step === s ? 'bg-[#6FA248] text-white shadow-[0_0_10px_rgba(111,162,72,0.3)]' : step > s ? 'bg-[#6FA248]/30 text-[#6FA248]' : 'bg-white/5 text-white/30')} data-id="element-2469">
                {step > s ? <Check className="w-3 h-3" data-id="element-2470" /> : s}
              </div>
              {s < 2 && <div className={cn('flex-1 h-0.5 mx-2 transition-colors', step > s ? 'bg-[#6FA248]/30' : 'bg-white/5')} data-id="element-2471" />}
            </Fragment>)}
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4" data-id="element-2472">
          {loading ? <div className="flex flex-col items-center justify-center py-12 text-white/50" data-id="element-2473">
              <Loader2 className="w-8 h-8 animate-spin mb-4 text-[#6FA248]" data-id="element-2474" />
              <p className="text-sm" data-id="element-2475">Cargando opciones...</p>
            </div> : <>
              {step === 1 && <div className="space-y-3 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2476">
                  <h3 className="text-sm font-bold text-white/90 mb-4" data-id="element-2477">
                    1. Seleccioná el tamaño
                  </h3>
                  {sizeOptions.length === 0 ? <p className="text-xs text-white/50 py-6 text-center" data-id="element-2478">
                      No hay tamaños configurados para esta pizza.
                    </p> : sizeOptions.map(opt => <button key={opt.id} onClick={() => setSelectedSize(opt.name)} className={cn('w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all', selectedSize === opt.name ? 'border-[#6FA248] bg-[#6FA248]/10' : 'border-white/5 bg-white/5 hover:border-white/20')} data-id="element-2479">
                        <span className="text-base font-bold capitalize" data-id="element-2480">
                          {opt.name}
                        </span>
                        <div className={cn('w-5 h-5 rounded-full border-2 flex items-center justify-center', selectedSize === opt.name ? 'border-[#6FA248] bg-[#6FA248]' : 'border-white/20')} data-id="element-2481">
                          {selectedSize === opt.name && <div className="w-2 h-2 rounded-full bg-white" data-id="element-2482" />}
                        </div>
                      </button>)}
                </div>}
              {step === 2 && <div className="space-y-3 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2483">
                  <div className="flex items-center justify-between mb-4" data-id="element-2484">
                    <h3 className="text-sm font-bold text-white/90" data-id="element-2485">
                      2. Elegí 5 sabores diferentes
                    </h3>
                    <span className={cn('text-xs font-black px-2 py-1 rounded-md', canConfirm ? 'bg-[#6FA248]/20 text-[#6FA248]' : 'bg-white/5 text-white/50')} data-id="element-2486">
                      {selectedFlavors.length}/{REQUIRED_FLAVORS}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-2" data-id="element-2487">
                    {flavorPizzas.map(pizza => {
                const idx = selectedFlavors.findIndex(f => f.id === pizza.id);
                const isSelected = idx >= 0;
                const atCap = !isSelected && selectedFlavors.length >= REQUIRED_FLAVORS;
                return <button key={pizza.id} onClick={() => toggleFlavor(pizza)} disabled={atCap} className={cn('w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all', isSelected ? 'border-[#6FA248] bg-[#6FA248]/10' : atCap ? 'border-white/5 bg-white/5 opacity-40 cursor-not-allowed' : 'border-white/5 bg-white/5 hover:border-white/20')} data-id="element-2488">
                          <div className="w-12 h-12 rounded-lg bg-black overflow-hidden shrink-0" data-id="element-2489">
                            {pizza.image_url ? <img src={pizza.image_url} alt={pizza.name} className="w-full h-full object-cover opacity-80" data-id="element-2490" /> : <div className="w-full h-full flex items-center justify-center bg-white/5" data-id="element-2491">
                                <Pizza className="w-5 h-5 text-white/20" data-id="element-2492" />
                              </div>}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-2493">
                            <p className="font-bold text-sm truncate text-white/90" data-id="element-2494">
                              {pizza.name}
                            </p>
                          </div>
                          <div className={cn('w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 text-xs font-black', isSelected ? 'border-[#6FA248] bg-[#6FA248] text-white' : 'border-white/20 text-white/40')} data-id="element-2495">
                            {isSelected ? idx + 1 : ''}
                          </div>
                        </button>;
              })}
                  </div>
                </div>}
            </>}
        </div>
        {/* Footer */}
        <div className="shrink-0 p-4 border-t border-white/10 bg-[#111]" data-id="element-2496">
          {step === 2 && <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/10" data-id="element-2497">
              <div className="flex justify-between items-center mb-2" data-id="element-2498">
                <span className="text-xs text-white/50 uppercase tracking-wider font-bold" data-id="element-2499">
                  Resumen
                </span>
                <span className="text-[10px] font-bold bg-[#6FA248]/20 text-[#6FA248] px-2 py-0.5 rounded capitalize" data-id="element-2500">
                  Tamaño {selectedSize}
                </span>
              </div>
              {selectedFlavors.length > 0 && <div className="space-y-0.5 mb-3" data-id="element-2501">
                  {selectedFlavors.map((f, i) => <div key={f.id} className="text-xs text-white/70 truncate" data-id="element-2502">
                      <span className="text-[#6FA248] font-bold mr-1.5" data-id="element-2503">
                        {i + 1}.
                      </span>
                      {f.name}
                    </div>)}
                </div>}
              <div className="h-px bg-white/10 w-full mb-3" data-id="element-2504" />
              <div className="flex justify-between items-end" data-id="element-2505">
                <span className="text-xs text-white/50" data-id="element-2506">Total a cobrar</span>
                <span className="text-xl font-black text-[#6FA248] tabular-nums" data-id="element-2507">
                  {formatCurrency(finalPrice)}
                </span>
              </div>
            </div>}
          <div className="flex gap-3" data-id="element-2508">
            {step > 1 && <Button variant="outline" onClick={handleBack} className="flex-1 border-white/20 text-white hover:bg-white/10" data-id="element-2509">
                Atrás
              </Button>}
            {step < 2 ? <Button onClick={handleNext} disabled={!selectedSize} className="flex-[2] bg-[#6FA248] hover:bg-[#5c873b] text-white font-bold" data-id="element-2510">
                Siguiente
              </Button> : <Button onClick={handleConfirm} disabled={!canConfirm} className="flex-[2] bg-[#6FA248] hover:bg-[#5c873b] text-white font-bold" data-id="element-2511">
                Agregar al pedido
              </Button>}
          </div>
        </div>
      </div>
    </Modal>;
}